import { useRef, useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Play, ChevronRight, ChevronLeft, Film, Video, Trophy, Users, Ticket, Award, Calendar, Eye, Star, Clapperboard, MapPin } from "lucide-react";
import { Reveal } from "./Reveal";
import { Modal, ModalScroll } from "./Modal";
import { useShowreel } from "./Showreel";
import { AwardLabel, filmAward, AnonSilhouette } from "./FestivalGraphics";
import { resolveUrl, galleryCover } from "../../lib/media";
import { frameStyle, frameAspectClass } from "../../lib/frame";

const STAT_ICONS = [Film, Video, Trophy, Users];
// Icon options selectable per stat from the admin.
export const STAT_ICON_MAP = { film: Film, video: Video, trophy: Trophy, users: Users, ticket: Ticket, award: Award, calendar: Calendar, eye: Eye, star: Star, clapperboard: Clapperboard, mapPin: MapPin };
export const STAT_ICON_OPTIONS = Object.keys(STAT_ICON_MAP);

const DEFAULT_HERO_SETTINGS = { brightness: 0.95, contrast: 1.18, saturate: 0.85, overlay: 0.5, glow: 1, fade: 1, blur: 0, zoom: 1, posX: 68, posY: 50, opacity: 1 };

// Shared style for the Festival background image — used by BOTH the Hero and the
// entry transition so the visual stays consistent everywhere.
export const heroImageStyle = (hs = {}) => ({
  filter: `brightness(${hs.brightness ?? 0.95}) contrast(${hs.contrast ?? 1.18}) saturate(${hs.saturate ?? 0.85}) blur(${hs.blur ?? 0}px)`,
  transform: `scale(${hs.zoom ?? 1})`,
  // Ancrage focal identique à frame.js (Mode Transformer) → l'aperçu admin = rendu site.
  transformOrigin: `${hs.posX ?? 68}% ${hs.posY ?? 50}%`,
  objectPosition: `${hs.posX ?? 68}% ${hs.posY ?? 50}%`,
  opacity: hs.opacity ?? 1,
});

// New default Karams Festival visual (deep black + electric blue, chair + laurels).
// NEVER fall back to the old red-cinema-seats background.
export const DEFAULT_HERO = "https://static.prod-images.emergentagent.com/jobs/6bc650ae-426d-402c-81c0-631a25d6080a/images/24c4f3cfd67d0704e944fbe6fffc21fb930f11d94f188966b36d33a484be84a1.jpeg";

// Single main visual per edition — drives BOTH the hero (current edition) and the
// edition card. If none, the blue default is used. No second image to manage.
export const editionVisual = (ed) => ed?.heroImage || ed?.poster || DEFAULT_HERO;

// Poster display formats (never stretched).
export const POSTER_ASPECT = { "2:3": "aspect-[2/3]", "16:9": "aspect-[16/9]", "1:1": "aspect-square", original: "min-h-[260px]" };
export const posterAspect = (fmt) => POSTER_ASPECT[fmt] || "aspect-[2/3]";
// Normalized orientation key for a poster format value ("2:3" | "16:9" | "1:1" | "original").
export const posterAspectKey = (fmt) => fmt === "2:3" ? "portrait" : fmt === "1:1" ? "square" : fmt === "original" ? "original" : "landscape";

// Poster framing inside a UNIFORM block: the card/block keeps a single fixed size
// everywhere; the admin frames the image with zoom + position.
// UNIFIÉ : film.posterFrame (Mode Transformer : posX/posY/zoom/fit/format/noBg/border/maxW)
// prime sur l'ancien film.detail — aucune migration nécessaire, l'ancien reste le repli.
export const POSTER_BLOCK = "aspect-[2/3]";
export const posterImgStyle = (film) => {
  const pf = film && film.posterFrame;
  if (pf && Object.keys(pf).length) {
    const s = frameStyle(pf, "cover");
    return { objectFit: s.objectFit, objectPosition: s.objectPosition, transform: s.transform, transformOrigin: s.transformOrigin };
  }
  const d = (film && film.detail) || {};
  if (!Object.keys(d).length) return undefined; // aucun cadrage → pas de style inline (préserve le survol CSS)
  return { transform: `scale(${d.zoom ?? 1})`, objectPosition: `${d.posX ?? 50}% ${d.posY ?? 50}%` };
};

// Aspect d'une CARTE affiche : posterFrame.format (Mode Transformer) > posterFormat (legacy) > 2/3.
// "original" sur une carte = bloc 2/3 en contain (affiche entière visible, grille homogène).
export const posterCardAspect = (film, fallback = POSTER_BLOCK) => {
  const pf = film && film.posterFrame;
  if (pf && pf.format && pf.format !== "auto") {
    if (pf.format === "original") return { cls: fallback, contain: true };
    return { cls: frameAspectClass(pf, fallback), contain: pf.fit === "contain" };
  }
  return { cls: fallback, contain: false };
};

const editionSlug = (ed) => ed?.id || ed?.year;

// ---------- Section chrome ----------
const SectionHead = ({ title, action }) => (
  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 mb-6">
    <h2 className="flex items-center gap-3 text-[13px] md:text-sm tracking-[0.28em] uppercase text-white font-semibold">
      <span className="h-4 w-[3px] bg-electric rounded-full" /> {title}
    </h2>
    {action}
  </div>
);

const Panel = ({ id, children, testid }) => (
  <section id={id} data-testid={testid} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 md:p-7">
    {children}
  </section>
);

// Horizontal scroller with overflow-aware arrows (hidden when nothing to scroll).
export const HScroll = ({ children, testid }) => {
  const ref = useRef(null);
  const [state, setState] = useState({ left: false, right: false });
  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setState({ left: el.scrollLeft > 8, right: el.scrollLeft < max - 8 });
  }, []);
  useEffect(() => {
    update();
    const el = ref.current;
    el?.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    const t = setTimeout(update, 600); // after images/layout settle
    return () => { el?.removeEventListener("scroll", update); window.removeEventListener("resize", update); clearTimeout(t); };
  }, [update]);
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * Math.min(ref.current.clientWidth * 0.85, 720), behavior: "smooth" });
  const arrowCls = "hidden md:flex absolute top-1/2 -translate-y-1/2 h-11 w-11 rounded-full border border-white/20 bg-[#0a0a0a]/90 backdrop-blur text-white/80 items-center justify-center hover:border-electric hover:text-electric transition-colors z-10";
  return (
    <div className="relative">
      <div ref={ref} data-testid={testid} className="flex gap-4 md:gap-5 overflow-x-auto pb-2 snap-x scroll-smooth" style={{ scrollbarWidth: "none" }}>
        {children}
      </div>
      {state.left && (
        <button onClick={() => scroll(-1)} aria-label="Précédent" data-testid={`${testid}-prev`} className={`${arrowCls} -left-3`}>
          <ChevronLeft size={20} />
        </button>
      )}
      {state.right && (
        <button onClick={() => scroll(1)} aria-label="Suivant" data-testid={`${testid}-next`} className={`${arrowCls} -right-3`}>
          <ChevronRight size={20} />
        </button>
      )}
    </div>
  );
};

// ---------- HERO ----------
export const FestivalHero = ({ festival = {}, edition, heroImage, onApply }) => {
  const showreel = useShowreel();
  const year = edition?.year || (festival.edition || "").match(/\d{4}/)?.[0] || "2026";
  const desc = edition?.description || festival.description;
  const reel = edition?.reelUrl || festival.reelUrl;
  const teaserFormat = edition?.teaserFormat || festival.teaserFormat || "auto";
  const hs = { ...DEFAULT_HERO_SETTINGS, ...(edition?.heroSettings || {}) };
  const imgStyle = heroImageStyle(hs);
  const defaultStats = [
    { value: "3", label: "Éditions", icon: "film" },
    { value: "30+", label: "Films diffusés", icon: "video" },
    { value: "18", label: "Prix remis", icon: "trophy" },
    { value: "600+", label: "Spectateurs", icon: "users" },
  ];
  const rawStats = festival.stats && festival.stats.length ? festival.stats : defaultStats;
  const stats = rawStats
    .map((s, i) => ({ ...s, _i: i }))
    .filter((s) => s.enabled !== false && (s.value || s.label))
    .sort((a, b) => (a.order ?? a._i) - (b.order ?? b._i))
    .slice(0, 6);

  return (
    <section className="relative w-full overflow-hidden pt-[74px]" data-testid="festival-hero">
      {/* Full-bleed cinematic background — merges into pure black. Admin image + visual settings supported. */}
      <div className="absolute inset-0" aria-hidden="true">
        {heroImage
          ? <img src={resolveUrl(heroImage)} alt="" style={imgStyle} className="festival-hero-img absolute inset-0 w-full h-full object-cover" />
          : <div className="absolute inset-0" style={{ background: "var(--fx-bg)" }} />}
        {/* adjustable dark overlay for contrast / legibility */}
        <div className="absolute inset-0 bg-black" style={{ opacity: hs.overlay }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/55 to-transparent" />
        <div className="hidden lg:block absolute inset-y-0 right-0 w-[24%] bg-gradient-to-l from-[#050505]/85 to-transparent" />
        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#050505] to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-transparent" style={{ opacity: Math.min(1, hs.fade ?? 1) }} />
        <div style={{ opacity: hs.glow ?? 1 }}>
          <div className="festival-halo festival-halo-1" />
          <div className="festival-halo festival-halo-2" />
        </div>
      </div>

      <div className="relative z-10 max-w-[1500px] mx-auto px-5 md:px-10 min-h-[clamp(440px,60vh,620px)] grid grid-cols-1 lg:grid-cols-12 gap-6 items-center py-10 lg:py-8">
        {/* Left — title + text + CTAs */}
        <div className="lg:col-span-6 order-2 lg:order-1">
          <Reveal>
            <div className="text-[11px] md:text-[12px] tracking-[0.35em] uppercase text-electric mb-2 font-semibold">Karams Festival</div>
            <h1 className="font-sans font-bold uppercase leading-[0.8] tracking-tight text-white text-6xl sm:text-7xl lg:text-8xl mb-3 lg:whitespace-nowrap">
              Édition <span className="text-electric" style={{ textShadow: "0 0 30px color-mix(in srgb, var(--fx-primary) 55%, transparent)" }}>{year}</span>
            </h1>
            {desc && <p className="text-sm md:text-base text-white/75 font-light leading-relaxed max-w-md mb-6">{desc}</p>}
            <div className="flex flex-wrap gap-3">
              {reel && (
                <button onClick={() => showreel.open(reel, `Karams Festival ${year}`, "Teaser", teaserFormat)} data-testid="festival-hero-teaser"
                  className="inline-flex items-center gap-2.5 px-6 h-11 rounded-md bg-electric text-[11px] tracking-[0.14em] uppercase font-semibold hover:brightness-110 transition-all" style={{ color: "var(--fx-on-primary)" }}>
                  <Play size={15} /> Découvrir le teaser
                </button>
              )}
            </div>
          </Reveal>
        </div>

        {/* Middle — the cinematic visual lives in the full-bleed background */}
        <div className="hidden lg:block lg:col-span-4 order-1" aria-hidden="true" />

        {/* Right — stats */}
        <div className="lg:col-span-2 order-3">
          <Reveal>
            <div className="grid grid-cols-2 lg:grid-cols-1 gap-y-5 gap-x-4 lg:border-l lg:border-white/10 lg:pl-6" data-testid="festival-stats">
              {stats.map((s, i) => {
                const Icon = STAT_ICON_MAP[s.icon] || STAT_ICONS[i] || Film;
                return (
                  <div key={i} className="flex items-center gap-3" data-testid={`festival-stat-${i}`}>
                    <Icon size={24} className="text-electric shrink-0" strokeWidth={1.5} />
                    <div>
                      <div className="font-sans font-bold text-2xl md:text-3xl text-white leading-none">{s.value}</div>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-white/55 mt-1">{s.label}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

// ---------- SÉLECTION OFFICIELLE ----------
export const FestivalFilmsRow = ({ films = [], year, edition, awards = [], onFilm }) => {
  if (films.length === 0) return null;
  return (
    <Panel id="films" testid="festival-selection">
      <SectionHead title={`Sélection officielle ${year}`}
        action={edition && (
          <Link to={`/festival/edition/${editionSlug(edition)}`} data-testid="festival-see-all-films"
            className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.16em] uppercase text-secondary2 hover:text-electric transition-colors">Voir tous les films <ChevronRight size={14} /></Link>
        )} />
      <HScroll testid="festival-selection-scroll">
        {films.map((film, i) => {
          const aw = filmAward(film);
          const poster = film.poster || galleryCover(film.media);
          const cardAsp = posterCardAspect(film);
          return (
            <button key={film.id || i} onClick={() => onFilm({ film, year })} data-testid={`festival-film-${i}`}
              className="group snap-start shrink-0 w-[160px] sm:w-[190px] text-left">
              <div className="h-6 mb-1.5 flex items-center justify-center">
                {aw && <AwardLabel name={aw.name} color={aw.color} size={aw.size} testid={`festival-film-award-${i}`} />}
              </div>
              <div className={`relative ${cardAsp.cls} rounded-lg overflow-hidden border border-white/10 bg-[#0e0e0e] group-hover:border-electric/50 transition-colors`}>
                {poster
                  ? <img src={resolveUrl(poster)} alt={film.title} loading="lazy" className="w-full h-full object-cover" style={posterImgStyle(film)} />
                  : <div className="w-full h-full flex items-center justify-center text-white/30 text-sm px-3 text-center">{film.title}</div>}
              </div>
              <div className="mt-2.5">
                <div className="fx-film-title text-sm text-white leading-snug line-clamp-1 group-hover:text-electric transition-colors">{film.title}</div>
                {film.duration && <div className="text-xs text-secondary2 mt-0.5">{film.duration}</div>}
              </div>
            </button>
          );
        })}
      </HScroll>
    </Panel>
  );
};

// ---------- PALMARÈS ----------
export const FestivalPalmaresRow = ({ entries = [], year, edition }) => {
  if (entries.length === 0) return null;
  return (
    <Panel id="palmares" testid="festival-palmares">
      <SectionHead title={`Palmarès ${year}`}
        action={(
          <Link to="/festival/palmares" data-testid="festival-see-all-palmares"
            className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.16em] uppercase text-secondary2 hover:text-electric transition-colors">Voir tout le palmarès <ChevronRight size={14} /></Link>
        )} />
      <HScroll testid="festival-palmares-scroll">
        {entries.map((e, i) => (
          <div key={i} className="snap-start shrink-0 w-[220px] text-center" data-testid={`festival-award-${i}`}>
            <div className="h-6 mb-2 flex items-center justify-center px-2">
              <AwardLabel name={e.award.name} color={e.award.color} size={e.award.size} />
            </div>
            <div className={`relative ${POSTER_BLOCK} w-[130px] mx-auto rounded-md overflow-hidden border border-white/10 bg-[#0e0e0e]`}>
              {e.poster
                ? <img src={resolveUrl(e.poster)} alt={e.film} loading="lazy" className="w-full h-full object-cover" style={posterImgStyle(e)} />
                : <div className="w-full h-full flex items-center justify-center text-white/30 text-xs px-2 text-center">{e.film}</div>}
            </div>
            <div className="text-sm text-white mt-2 line-clamp-1">{e.film}</div>
          </div>
        ))}
      </HScroll>
    </Panel>
  );
};

// ---------- JURY ----------
export const JuryMemberModal = ({ member, onClose }) => {
  const m = member || {};
  return (
    <Modal open={!!member} onClose={onClose} size="lg" testid="festival-jury-modal">
      <ModalScroll>
        <div className="p-6 md:p-8 flex flex-col sm:flex-row gap-6">
          <div className="w-full sm:w-44 shrink-0">
            <div className="aspect-[3/4] rounded-xl overflow-hidden border border-white/10 bg-[#0e0e0e]">
              {m.photo ? <img src={resolveUrl(m.photo)} alt={m.name} className="w-full h-full object-cover" /> : <AnonSilhouette className="w-full h-full" />}
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-serif text-3xl text-white leading-tight" data-testid="jury-modal-name">{m.name}</h3>
            {m.role && <div className="text-[11px] tracking-[0.16em] uppercase text-electric mt-2">{m.role}</div>}
            {m.bio
              ? <div className="mt-5 space-y-3 text-white/75 font-light leading-relaxed" data-testid="jury-modal-bio">{String(m.bio).split(/\n{2,}/).map((p, i) => <p key={i}>{p.trim()}</p>)}</div>
              : <p className="mt-5 text-secondary2 font-light">Biographie à venir.</p>}
          </div>
        </div>
      </ModalScroll>
    </Modal>
  );
};

export const FestivalJuryRow = ({ members = [], year, colorMode = "hover", onJury }) => {
  const [sel, setSel] = useState(null);
  if (members.length === 0) return null;
  const imgFilter = colorMode === "color" ? "" : "grayscale hover:grayscale-0";
  return (
    <Panel id="jury" testid="festival-jury">
      <SectionHead title={`Le jury ${year}`}
        action={(
          <button onClick={onJury} data-testid="festival-see-all-jury"
            className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.16em] uppercase text-secondary2 hover:text-electric transition-colors">Découvrir le jury complet <ChevronRight size={14} /></button>
        )} />
      <HScroll testid="festival-jury-scroll">
        {members.map((m, i) => (
          <button key={m.id || i} onClick={() => setSel(m)} data-testid={`festival-jury-card-${i}`}
            className="snap-start shrink-0 w-[260px] flex items-center gap-4 rounded-xl border border-white/10 bg-white/[0.02] p-3 text-left hover:border-electric/40 transition-colors group">
            <div className="h-16 w-16 rounded-lg overflow-hidden border border-white/10 bg-[#0e0e0e] shrink-0">
              {m.photo
                ? <img src={resolveUrl(m.photo)} alt={m.name} loading="lazy" className={`w-full h-full object-cover transition-[filter] duration-500 ${imgFilter}`} />
                : <AnonSilhouette className="w-full h-full" />}
            </div>
            <div className="min-w-0">
              <div className="font-serif text-lg text-white leading-tight line-clamp-2 group-hover:text-electric transition-colors">{m.name}</div>
              {m.role && <div className="text-[10px] tracking-[0.14em] uppercase text-electric mt-1 line-clamp-1">{m.role}</div>}
            </div>
          </button>
        ))}
      </HScroll>
      <JuryMemberModal member={sel} onClose={() => setSel(null)} />
    </Panel>
  );
};

// ---------- ÉDITIONS ----------
export const FestivalEditionsStrip = ({ editions = [], currentId }) => {
  if (editions.length === 0) return null;
  return (
    <Panel id="editions" testid="festival-editions-strip">
      <SectionHead title="Les éditions" />
      <div data-testid="festival-editions-grid" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
        {editions.map((ed, i) => {
          const isCurrent = editionSlug(ed) === currentId;
          const cover = editionVisual(ed);
          return (
            <Link key={ed.id || i} to={`/festival/edition/${editionSlug(ed)}`} data-testid={`festival-edition-card-${ed.year}`}
              style={isCurrent ? { boxShadow: "0 0 0 1px color-mix(in srgb, var(--fx-primary) 50%, transparent), 0 18px 50px -20px color-mix(in srgb, var(--fx-primary) 55%, transparent)" } : undefined}
              className={`group relative aspect-[3/4] rounded-2xl overflow-hidden border transition-all duration-500 ${isCurrent ? "border-electric" : "border-white/10 hover:border-electric/60"}`}>
              <img src={resolveUrl(cover)} alt={`Édition ${ed.year}`} loading="lazy"
                className={`absolute inset-0 w-full h-full object-cover ${isCurrent ? "opacity-75" : "opacity-50"} group-hover:opacity-95 group-hover:scale-[1.06] transition-all duration-700 ease-out`} />
              {/* cinematic black + edition-colour treatment, consistent with the festival universe */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/45 to-transparent" />
              <div className="absolute inset-0 opacity-70 group-hover:opacity-100 transition-opacity duration-500"
                style={{ background: "radial-gradient(120% 90% at 50% 120%, color-mix(in srgb, var(--fx-primary) 28%, transparent), transparent 60%)" }} />
              <div className="absolute inset-0 p-4 sm:p-5 flex flex-col justify-end">
                {isCurrent && <div className="text-[9px] sm:text-[10px] tracking-[0.22em] uppercase text-electric font-semibold mb-1.5">Édition actuelle</div>}
                <div className={`font-sans font-bold leading-none ${isCurrent ? "text-electric" : "text-white"} text-4xl sm:text-5xl`}>{ed.year}</div>
                <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] tracking-[0.16em] uppercase text-white/80 group-hover:text-white transition-colors">
                  Découvrir l'édition
                  <ChevronRight size={14} className="text-electric transition-transform duration-300 group-hover:translate-x-1" />
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </Panel>
  );
};
